import React, { useState, useRef } from "react"
import dayjs from "dayjs"
import { useQRCode } from "next-qrcode"
import TicketModal from "components/Modal/TicketModal"
import { useLang } from "hooks/useLang"
import Button from "components/Form/Button"
import CustomImage from "components/atoms/Image/CustomImage"
import { IFinalEvent } from "types/event"

interface IProps {
    ticket_end_time: string
    ticket_id: string
    ticket_number: string
    ticket_price: number
    ticket_start_time: string
    ticket_status: string
    ticket_title: string
    ticket_token: string

    event_city: string
    poster: string
    event_title: string
    seat_id: string
    index?: number
    lastIndex?: number
    isPast?: boolean
    pdf_url?: string
    ticket_type_id?: string
    event_id?: string
    image?: string
    event?: IFinalEvent
}

const TicketDetailCard = ({
    ticket_start_time,
    ticket_end_time,
    ticket_number,
    ticket_status,
    ticket_title,
    ticket_id,
    ticket_price,
    ticket_token,
    event_city,
    poster,
    event_title,
    seat_id,
    index,
    lastIndex,
    isPast,
    pdf_url,
    event_id,
    image,
    ticket_type_id,
    event,
}: IProps) => {
    const { t, isAr } = useLang()
    const [openModal, setOpenModal] = useState(false)
    const { Canvas } = useQRCode()
    const printRef = useRef<HTMLDivElement>(null)

    // Battarji STATIC handling for invitation
    const shouldInvitationButtonShow = () => {
        const REGULAR_BATTERJEE_TICKET_TYPE_ID = "664dc2f480415f1471134179"
        if (ticket_type_id !== REGULAR_BATTERJEE_TICKET_TYPE_ID) return true
        return false
    }

    return (
        <div
            ref={printRef}
            className={`overflow-hidden rounded-md bg-white text-sm capitalize shadow-3xl dark:bg-white md:text-base ${
                isPast ? "grayscale-[1]" : "grayscale-[0]"
            }`}
        >
            {/* Image container */}
            <div className="relative h-[180px] w-full">
                <CustomImage
                    src={poster}
                    alt={`poster-${poster}`}
                    fill
                    style={{
                        filter: isPast ? "grayscale(1)" : "",
                        objectFit: "cover",
                    }}
                />
            </div>

            {/* info cantainer */}
            <div className="flex justify-between p-5">
                {/* Left side */}
                <div className="flex flex-col justify-between">
                    <h1 className="font-bold dark:text-dark_gray">
                        {ticket_title}
                    </h1>
                    <div className="flex flex-col gap-4">
                        <div>
                            <div className="font-semibold dark:text-dark_gray">
                                {t("date")}
                            </div>
                            <div className="text-sm dark:text-dark_gray">
                                {dayjs(ticket_start_time).format("DD-MM-YYYY")}
                            </div>
                        </div>
                        <div>
                            <div className="font-semibold dark:text-dark_gray">
                                {t("location")}
                            </div>
                            <div className="text-sm dark:text-dark_gray">
                                {event_id === "6909f62832ddf632c5aca613" ? (
                                    <span
                                        className="cursor-pointer text-blue-800 hover:underline"
                                        onClick={() => {
                                            window.open(
                                                `https://maps.google.com/?q=${event?.address?.location?.lat},${event?.address?.location?.long}`,
                                                "_blank"
                                            )
                                        }}
                                    >
                                        {isAr
                                            ? "فندق فوكو جدة"
                                            : "Voco Jeddah Gate by IHG"}
                                    </span>
                                ) : (
                                    <span
                                        className="cursor-pointer text-blue-800 hover:underline"
                                        onClick={() => {
                                            window.open(
                                                `https://maps.google.com/?q=${event?.address?.location?.lat},${event?.address?.location?.long}`,
                                                "_blank"
                                            )
                                        }}
                                    >
                                        {t(event_city)}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* STATIC */}
                        {event_id !== "644e6db1395d499b300c589f" ? (
                            <div>
                                <div className="font-semibold dark:text-dark_gray">
                                    {t("time")}
                                </div>
                                <div className="flex items-center gap-2 text-sm dark:text-dark_gray">
                                    {dayjs(ticket_start_time).format("hh:mm A")}
                                    <div>-</div>
                                    {dayjs(ticket_end_time).format("hh:mm A")}
                                </div>
                            </div>
                        ) : (
                            <div className="h-7" />
                        )}

                        <div className="flex items-center gap-2">
                            <p className="text-sm font-bold capitalize opacity-30 dark:text-dark_gray">
                                {index} {t("of")} {lastIndex}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Right side */}
                <div className="flex flex-col items-center justify-between gap-10">
                    <div className="flex flex-col gap-2 text-center dark:text-dark_gray">
                        <p>
                            {t("ticket_number")}
                            <span className="block">{ticket_number}</span>
                        </p>
                        <div className="mx-auto h-[95px] w-[95px]">
                            {dayjs().isBefore(dayjs(ticket_end_time)) && (
                                <Canvas
                                    text={ticket_token}
                                    options={{
                                        width: 100,
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-0">
                        <div className="flex h-[50px] flex-col dark:text-dark_gray">
                            {seat_id && (
                                <>
                                    <h3 className="font-semibold ">
                                        {t("zone")}
                                    </h3>
                                    <p>{seat_id}</p>
                                </>
                            )}
                        </div>
                        {dayjs().isBefore(dayjs(ticket_end_time)) ? (
                            <div className="flex items-center gap-2 ">
                                {/* <Button
                                    size="small"
                                    className="text-xs capitalize"
                                    onClick={() => setOpenModal(true)}>
                                    {t("view_ticket")}
                                </Button>
                                <a
                                    href={pdf_url}
                                    // target="_blank"
                                    className="flex h-[40px] cursor-pointer items-center justify-center rounded-md px-3 transition-all hover:bg-gray-100 hover:shadow-sm dark:hover:bg-dark_header">
                                    <DownloadIcon />
                                </a> */}
                                {shouldInvitationButtonShow() && (
                                    <Button
                                        //size="large"
                                        className="rounded-full px-4 py-2 text-sm font-bold text-white transition-all md:px-6 bg-mint-green-500"
                                    >
                                        <a
                                            href={pdf_url}
                                            className="flex h-full w-full items-center justify-center text-center"
                                            // target="_blank"
                                        >
                                            {t("download")}
                                        </a>
                                    </Button>
                                )}
                            </div>
                        ) : (
                            // For design
                            <div className="h-8 md:h-10" />
                        )}
                    </div>
                </div>
            </div>

            {openModal && (
                <TicketModal
                    ticket_end_time={ticket_end_time}
                    ticket_id={ticket_id}
                    ticket_number={ticket_number}
                    ticket_price={ticket_price}
                    ticket_start_time={ticket_start_time}
                    ticket_status={ticket_status}
                    ticket_title={ticket_title}
                    ticket_token={ticket_token}
                    event_city={event_city}
                    setOpenModal={setOpenModal}
                    event_title={event_title}
                    seat_id={seat_id}
                />
            )}
        </div>
    )
}

export default TicketDetailCard
